import { getStore } from 'cadenza/store/cadenza-store';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { AttributeBundleDefinitionDto } from 'cadenza/workbook/model/attribute-bundle-definition-dto';
import type { AttributeBundleName } from 'cadenza/workbook/model/attribute-bundle-name';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { isAttributeToOneReachableFrom } from 'cadenza/workbook/state/workbook-reachability.state';
import {
  getAttributeById,
  getAttributeIdsFromBundle,
  getDataViewById,
  getViewTypeByDataViewId,
} from 'cadenza/workbook/workbook-core-selectors.state';

/**
 * Check the cardinality between the restricting attribute (for maps, the geometry) and the dimension attributes in some restricted bundles.
 *
 * The path from the restricting attribute to the other one must be only one-to-one or many-to-one associations,
 * otherwise we produce wrong SQL statements and render N geometries pro features.
 *
 * Note that measures are not restricted because they are aggregated anyway.
 *
 * There are 2 uses cases where this validator can block the operation:
 * <ul>
 *   <li>The restricting attribute is already added, and an attribute is being added to one of the restricted bundles.</li>
 *   <li>The restricting attribute is being added, and at least one of the restricted bundles is already populated.</li>
 * </ul>
 *
 * At the moment restricting attribute may be one of the following:
 * - for maps, geometry attribute
 * - for charts, last category attribute (coloring bundle is restricted)
 * @see AttributeRelationshipCardinalityBundleValidator
 * @param dataViewId The data view where an attribute is added
 * @param targetBundleDefinition The bundle definition of the bundle where the target attribute that is added
 * @param validatedAttribute The attribute that is being added
 */
export function validateBundleAttributeRelationCardinality(
  dataViewId: DataViewId,
  targetBundleDefinition: AttributeBundleDefinitionDto,
  validatedAttribute: WorkbookAttributeDto,
): boolean {
  if (isGeometryBundle(targetBundleDefinition)) {
    return validateAddingToGeometryBundle(dataViewId, validatedAttribute);
  } else {
    const targetBundleIsRestricted = targetBundleDefinition.restrictToOneCardinalityFromGeometryAttributeBundle;
    const restrictingAttribute = restrictingAttributeProvider(dataViewId);
    const shouldValidate = targetBundleIsRestricted && !!restrictingAttribute;
    return shouldValidate ? validateAddingToRestrictedBundle(validatedAttribute, restrictingAttribute) : true;
  }
}

/**
 * - for maps, restricting attribute is first geometry attribute
 * - for charts, restricting attribute is last category attribute
 * @see IRestrictingAttributeProvider:
 */
function restrictingAttributeProvider(dataViewId: DataViewId) {
  const viewType = getViewTypeByDataViewId(getStore().getState(), dataViewId);
  if (viewType === 'MAP') {
    return getAttributeIdsFromBundle(getStore().getState(), dataViewId, 'GEOMETRY').at(0);
  }
  if (viewType === 'CHART') {
    return supportsSubCategoryBundle(dataViewId) ?
        getAttributeIdsFromBundle(getStore().getState(), dataViewId, 'SUB_CATEGORY').at(0)
      : getAttributeIdsFromBundle(getStore().getState(), dataViewId, 'DIMENSION').at(-1);
  }
}

function supportsSubCategoryBundle(dataViewId: DataViewId) {
  const dataView = getDataViewById(getStore().getState(), dataViewId);
  return dataView.attributeBundleDefinitions['SUB_CATEGORY'] != null;
}

function validateAddingToGeometryBundle(dataViewId: DataViewId, validatedAttribute: WorkbookAttributeDto): boolean {
  const attributesByBundle = getDataViewById(getStore().getState(), dataViewId).attributeBundleDefinitions;
  const cardinalityRestrictedBundles = Object.entries(attributesByBundle)
    .filter(([_, definition]) => definition.restrictToOneCardinalityFromGeometryAttributeBundle)
    .map(([name]) => name as AttributeBundleName);
  const attributesFromRestrictedBundles = cardinalityRestrictedBundles
    .flatMap((restrictedBundleName) =>
      getAttributeIdsFromBundle(getStore().getState(), dataViewId, restrictedBundleName),
    )
    .map((attrId) => getAttributeById(getStore().getState(), attrId));

  // If the restricted bundles are empty, anything can be added to the geometry bundle
  if (!attributesFromRestrictedBundles.length) {
    return true;
  }

  // Check that every attribute in one of the restricted bundles has the correct cardinality
  return attributesFromRestrictedBundles.every(
    (attributeInRestrictedBundle) =>
      attributeInRestrictedBundle.category === 'MEASURE' ||
      isAttributeToOneReachableFrom(getStore().getState(), validatedAttribute, attributeInRestrictedBundle),
  );
}

function validateAddingToRestrictedBundle(
  validatedAttribute: WorkbookAttributeDto,
  restrictingAttribute: ActiveAttributeId,
) {
  if (validatedAttribute.category === 'MEASURE') {
    // No ToOneReachability check needed for measures
    // (and attribute type validations are done in attribute-bundle-validator.ts#isAcceptedAttributeType and should not be duplicated here)
    return true;
  }
  const attrFromGeometryBundle = getAttributeById(getStore().getState(), restrictingAttribute);
  return isAttributeToOneReachableFrom(getStore().getState(), attrFromGeometryBundle, validatedAttribute);
}

function isGeometryBundle(targetBundleDefinition: AttributeBundleDefinitionDto) {
  return targetBundleDefinition.attributeBundleName === 'GEOMETRY';
}
